/**
 * Authentication context for managing user state
 */

import type { ReactNode } from 'react';
import React, { createContext, useContext, useState } from 'react';
import { auth, type UserRole } from '../lib/auth';

interface AuthContextType {
  token: string | null;
  googleCredential: string | null;
  userEmail: string | null;
  userRole: UserRole | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (email: string, role: UserRole) => void;
  loginWithGoogle: (credential: string, email: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  // Initialize from localStorage immediately (not in useEffect to avoid re-render)
  const [token, setToken] = useState<string | null>(() => auth.getToken());
  const [userRole, setUserRole] = useState<UserRole | null>(() => auth.getRole());
  const [googleCredential, setGoogleCredential] = useState<string | null>(() =>
    auth.getGoogleCredential()
  );

  const login = (email: string, role: UserRole) => {
    auth.setToken(email);
    auth.setRole(role);
    setToken(email);
    setUserRole(role);
  };

  const loginWithGoogle = (credential: string, email: string) => {
    auth.setGoogleCredential(credential);
    auth.setToken(email);
    auth.setRole('admin');
    setGoogleCredential(credential);
    setToken(email);
    setUserRole('admin');
  };

  const logout = () => {
    auth.clearAuth();
    setToken(null);
    setUserRole(null);
    setGoogleCredential(null);
  };

  const value: AuthContextType = {
    token,
    googleCredential,
    userEmail: token,
    userRole,
    isAuthenticated: token !== null,
    isAdmin: userRole === 'admin',
    login,
    loginWithGoogle,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
